import { UserRole } from "@/services/types/common.types";
import type {
  InstituteInfo,
  StudentInfo,
  TenantInfo,
  User,
} from "@/services/types/common.types";
import type { AuthTokens } from "@/services/types/auth.types";

/**
 * Pure parsers for the auth API payloads (no network, store or axios imports).
 *
 * TOKENS (login / refresh) - confirmed login contract:
 *   { access_token, refresh_token, token_type, expires_in, refresh_expires_in }
 *   (expires_* are SECONDS). Also tolerated: camelCase keys, a { success, data }
 *   envelope and a { tokens: {...} } wrapper (old contract).
 *
 * GET api/v1/auth/me - shape CONFIRMED from the backend sample (see `MeResponse`):
 *   {
 *     id (number), tenant_id, institute_id, first_name, last_name,
 *     role (single string, e.g. "super_admin"),
 *     board, class, academic_year, section, medium, designation, primary_subject,
 *     status ("active"),
 *     institute: { institute_name, state, city_village, pincode, academic_start, academic_end }
 *   }
 *   It is flat (no envelope) and has NO email: the login form email is passed in
 *   via `options.email` (or merged from the persisted user at startup).
 *
 * The mapper stays tolerant of other spellings (kept from the earlier assumed shape):
 *   a { success, data } and/or { user } envelope; id | user_id | userId | sub;
 *   name | full_name | display_name; roles: [...] or role objects { name | code };
 *   tenant | organization: { id, name }; nested profile: {...}; scopes; avatar.
 *   Missing fields default to "" / [] so the rest of the app keeps working.
 *
 * Roles: unrecognized role strings are dropped with ONE console.warn (role string
 * only). authService.getCurrentUser then rejects a user with zero recognized roles.
 */

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pickString(source: UnknownRecord, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }
  return undefined;
}

function pickId(source: UnknownRecord, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
}

function pickNumber(source: UnknownRecord, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (value === undefined || value === null || value === "") continue;
    const parsed = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

/** Unwrap an optional { success, data } envelope. */
function unwrapEnvelope(raw: unknown): unknown {
  if (isRecord(raw) && isRecord(raw.data)) {
    return raw.data;
  }
  return raw;
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export function parseTokenResponse(raw: unknown): AuthTokens | null {
  let body = unwrapEnvelope(raw);
  if (isRecord(body) && isRecord(body.tokens)) {
    body = body.tokens;
  }
  if (!isRecord(body)) {
    return null;
  }

  const accessToken = pickString(body, ["access_token", "accessToken"]);
  if (!accessToken) {
    return null;
  }

  return {
    accessToken,
    refreshToken: pickString(body, ["refresh_token", "refreshToken"]),
    tokenType: pickString(body, ["token_type", "tokenType"]),
    expiresIn: pickNumber(body, ["expires_in", "expiresIn"]),
    refreshExpiresIn: pickNumber(body, [
      "refresh_expires_in",
      "refreshExpiresIn",
    ]),
  };
}

// ---------------------------------------------------------------------------
// /me -> User
// ---------------------------------------------------------------------------

/** A school_admin maps to ADMIN (first, so it stays the primary role) plus the SCHOOL_ADMIN marker. */
const ROLE_ALIASES: Record<string, UserRole | UserRole[]> = {
  tenant_admin: UserRole.ADMIN,
  tenantadmin: UserRole.ADMIN,
  school_admin: [UserRole.ADMIN, UserRole.SCHOOL_ADMIN],
  admin: UserRole.ADMIN,
  platform_admin: UserRole.PLATFORM_ADMIN,
  platformadmin: UserRole.PLATFORM_ADMIN,
  super_admin: UserRole.PLATFORM_ADMIN,
  superadmin: UserRole.PLATFORM_ADMIN,
  creator: UserRole.CREATOR,
  content_creator: UserRole.CREATOR,
  contentcreator: UserRole.CREATOR,
};

/** Error code / message used when /me yields no role this app understands. */
export const UNSUPPORTED_ROLE_CODE = "UNSUPPORTED_ROLE";
export const UNSUPPORTED_ROLE_MESSAGE =
  "Your account role is not supported in this app";

function roleName(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value.trim() || undefined;
  }
  if (isRecord(value)) {
    return pickString(value, ["name", "code", "role", "slug"]);
  }
  return undefined;
}

function mapRoles(source: UnknownRecord): UserRole[] {
  const candidates: unknown[] = [];
  if (Array.isArray(source.roles)) {
    candidates.push(...source.roles);
  } else if (source.roles !== undefined) {
    candidates.push(source.roles);
  }
  if (source.role !== undefined) {
    candidates.push(source.role);
  }

  const roles: UserRole[] = [];
  const unmapped: string[] = [];
  for (const candidate of candidates) {
    const name = roleName(candidate);
    if (!name) continue;
    const key = name.toLowerCase().replace(/[\s-]+/g, "_");
    const mapped = ROLE_ALIASES[key];
    if (mapped) {
      for (const role of Array.isArray(mapped) ? mapped : [mapped]) {
        if (!roles.includes(role)) roles.push(role);
      }
    } else if (!unmapped.includes(name)) {
      unmapped.push(name);
    }
  }

  if (unmapped.length > 0) {
    // Only the role string is logged (no tokens or other user data)
    console.warn(
      `[auth] Unrecognized role from /me: ${unmapped.join(", ")}`,
    );
  }
  return roles;
}

function mapInstitute(source: UnknownRecord): InstituteInfo | undefined {
  const raw = source.institute;
  if (!isRecord(raw)) return undefined;
  return {
    instituteName:
      pickString(raw, ["institute_name", "instituteName", "name"]) ?? "",
    state: pickString(raw, ["state"]) ?? "",
    cityVillage: pickString(raw, ["city_village", "cityVillage", "city"]) ?? "",
    pincode: pickId(raw, ["pincode", "pin_code"]) ?? "",
    academicStart: pickString(raw, ["academic_start", "academicStart"]) ?? "",
    academicEnd: pickString(raw, ["academic_end", "academicEnd"]) ?? "",
  };
}

function mapTenant(
  source: UnknownRecord,
  instituteName: string | undefined,
): TenantInfo {
  const nested = isRecord(source.tenant)
    ? source.tenant
    : isRecord(source.organization)
      ? source.organization
      : undefined;

  if (nested) {
    return {
      id: pickId(nested, ["id", "tenant_id", "tenantId"]) ?? "",
      name: pickString(nested, ["name", "tenant_name", "tenantName"]) ?? "",
    };
  }

  return {
    id: pickId(source, ["tenant_id", "tenantId"]) ?? "",
    name:
      pickString(source, ["tenant_name", "tenantName"]) ?? instituteName ?? "",
  };
}

function mapScopes(source: UnknownRecord): string[] {
  for (const key of ["scopes", "scope", "permissions"]) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim().split(/\s+/);
    }
  }
  return [];
}

export interface MapMeOptions {
  /** Fallback email (the /me response has none): the login form value or the persisted user's email. */
  email?: string;
}

export function mapMeResponseToUser(
  raw: unknown,
  options: MapMeOptions = {},
): User | null {
  let body = unwrapEnvelope(raw);
  if (isRecord(body) && isRecord(body.user)) {
    body = body.user;
  }
  if (!isRecord(body)) {
    return null;
  }

  const email =
    pickString(body, ["email"]) ?? (options.email?.trim() || undefined);
  const id = pickId(body, ["id", "user_id", "userId", "sub"]);
  if (!id && !email) {
    return null;
  }

  const firstName = pickString(body, ["first_name", "firstName"]) ?? "";
  const lastName = pickString(body, ["last_name", "lastName"]) ?? "";
  const joinedName = [firstName, lastName].filter(Boolean).join(" ");
  const name =
    pickString(body, [
      "name",
      "full_name",
      "fullName",
      "display_name",
      "displayName",
    ]) ??
    (joinedName || undefined) ??
    (email ? email.split("@")[0] : "");

  const institute = mapInstitute(body);
  const tenant = mapTenant(body, institute?.instituteName);
  // Real /me is flat (board, class, ... at the top level); a nested `profile` object is still tolerated
  const profileSource = isRecord(body.profile) ? body.profile : {};
  const pick = (keys: string[]): string =>
    pickString(profileSource, keys) ?? pickString(body, keys) ?? "";

  const profile: StudentInfo = {
    name,
    firstName: pickString(profileSource, ["first_name", "firstName"]) ?? firstName,
    lastName: pickString(profileSource, ["last_name", "lastName"]) ?? lastName,
    board: pick(["board"]),
    class: pick(["class", "class_name", "className"]),
    section: pick(["section"]),
    academicYear: pick(["academic_year", "academicYear"]),
    // WelcomeBanner uses `??`, so fall back to the institute/tenant name instead of ""
    institute: pickString(profileSource, ["institute"]) ?? tenant.name,
  };

  return {
    id: id ?? email ?? "",
    email: email ?? "",
    name,
    username: pickString(body, ["username", "user_name"]) ?? email ?? "",
    roles: mapRoles(body),
    avatar: pickString(body, ["avatar", "avatar_url", "avatarUrl", "picture"]),
    createdAt: pickString(body, ["created_at", "createdAt"]) ?? "",
    updatedAt: pickString(body, ["updated_at", "updatedAt"]) ?? "",
    scopes: mapScopes(body),
    profile,
    tenant,
    instituteId: pickId(body, ["institute_id", "instituteId"]),
    institute,
    status: pickString(body, ["status"]),
    designation: pickString(body, ["designation"]),
    primarySubject: pickString(body, ["primary_subject", "primarySubject"]),
    medium: pickString(body, ["medium"]),
  };
}
